import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	getContentKeywords,
	getTitleKeywords,
} from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalKeywords.ts';
import type { MeetingSignalInput } from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalTypes.ts';

import { createFireOnceGuard } from './createFireOnceGuard.ts';
import { evaluateAndMaybeFireMeetingSignal } from './evaluateAndMaybeFireMeetingSignal.ts';

export type DetectMeetingSignalInput = MeetingSignalInput;

export type DetectMeetingSignal = (input: DetectMeetingSignalInput) => boolean;

/**
 * The single hook every surface uses for the `meetingSignal detected`
 * instrumentation (STARB-3763). It owns the cross-cutting plumbing — the
 * analytics event factory, one in-memory fire-once guard per mount (so a
 * repeated caller, e.g. a debounced content stream, still fires at most once),
 * and the locale-resolved keyword lists — and returns a stable `detect`
 * callback. The caller decides *when* to call it and *which* signals to pass;
 * the gate check lives inside `evaluateAndMaybeFireMeetingSignal`.
 *
 * Pass `titleText` to match the title list, `contentText` (already plain text)
 * to match the content list, `template` to test the meeting-template set.
 */
export const useMeetingSignalDetection = (): DetectMeetingSignal => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const guardRef = useRef(createFireOnceGuard());

	const titleKeywords = useMemo(() => getTitleKeywords(intl), [intl]);
	const contentKeywords = useMemo(() => getContentKeywords(intl), [intl]);

	return useCallback(
		({ titleText, contentText, ...rest }: DetectMeetingSignalInput) =>
			evaluateAndMaybeFireMeetingSignal({
				...rest,
				titleText,
				contentText,
				createAnalyticsEvent,
				guard: guardRef.current,
				titleKeywords,
				contentKeywords,
			}),
		[createAnalyticsEvent, titleKeywords, contentKeywords],
	);
};
